import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';

import { PlusIcon } from '../../../components/icons';
import { ContainedButton } from '../../../components/buttons';

import { Style } from '../../styles/types';
import { DropdownOption } from '../../labels/components/settings/DropdownOption';
import { useStyles } from '../../styles/routes/hooks';

interface BaseStylesMultiSelectProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  selectedStyleIds: string[];
  onStylesChange: (styleIds: string[]) => void;
  error?: boolean;
}

export function BaseStylesMultiSelect({
  isOpen,
  setIsOpen,
  selectedStyleIds,
  onStylesChange,
  error
}: BaseStylesMultiSelectProps) {
  // Fetch only base styles (partner_id IS NULL)
  const { styles: fetchedStyles, isLoading } = useStyles({
    filterType: 'base'
  });
  const [localSelectedIds, setLocalSelectedIds] =
    useState<string[]>(selectedStyleIds);

  // Sync local state when prop changes
  useEffect(() => {
    setLocalSelectedIds(selectedStyleIds);
  }, [selectedStyleIds]);

  // Create style options with "All" option
  const styles = useMemo(
    () => [
      {
        style_id: 'all',
        system_name: 'All Base Styles',
        display_name: 'All Base Styles'
      } as Style,
      ...fetchedStyles
    ],
    [fetchedStyles]
  );

  const styleNames = useMemo(
    () =>
      new Map(styles.map(s => [s.style_id, s.display_name || s.system_name])),
    [styles]
  );

  // Check if "all" is selected
  const isAllSelected = localSelectedIds.includes('all');

  // Get display text for button
  const getDisplayText = (): string => {
    if (isAllSelected) {
      return 'All Base Styles';
    }
    if (localSelectedIds.length === 0) {
      return 'Select Base Styles';
    }
    return `${localSelectedIds.length} Base Style${
      localSelectedIds.length !== 1 ? 's' : ''
    }`;
  };

  const handleToggleStyle = (styleId: string) => {
    let newSelectedIds: string[];

    if (styleId === 'all') {
      // Toggle "all" selection
      if (isAllSelected) {
        newSelectedIds = [];
      } else {
        newSelectedIds = ['all'];
      }
    } else {
      // Toggle individual style
      if (localSelectedIds.includes(styleId)) {
        // Remove style
        newSelectedIds = localSelectedIds.filter(
          id => id !== styleId && id !== 'all'
        );
      } else {
        // Add style, remove "all" if present
        newSelectedIds = [
          ...localSelectedIds.filter(id => id !== 'all'),
          styleId
        ];
      }
    }

    setLocalSelectedIds(newSelectedIds);
  };

  const handleDone = () => {
    onStylesChange(localSelectedIds);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setLocalSelectedIds(selectedStyleIds);
    setIsOpen(false);
  };

  return (
    <div className="w-1/3">
      <label className="text-light-gray mb-1 block text-sm">Base Styles</label>
      <button
        className={clsx(
          'text-input-shadow flex h-[2.5rem] w-full items-center justify-between gap-4 overflow-hidden rounded-lg p-3',
          error && 'border-error border-2'
        )}
        onClick={() => {
          if (!isLoading) {
            setIsOpen(true);
          }
        }}
        disabled={isLoading}
      >
        <span className="truncate">
          {isLoading ? 'Loading...' : getDisplayText()}
        </span>
        <div className="text-aqua">
          <PlusIcon />
        </div>
      </button>

      {isOpen && (
        <div className="relative w-full">
          <div className="text-input-shadow absolute top-2 z-10 flex w-full flex-col rounded-lg bg-white">
            <div className="flex max-h-[40vh] w-full flex-col overflow-y-scroll">
              {styles.map(style => {
                const styleName = styleNames.get(style.style_id) ?? 'Unknown';
                const id = style.style_id ?? '';
                const isSelected =
                  id === 'all' ? isAllSelected : localSelectedIds.includes(id);

                return (
                  <DropdownOption
                    key={id}
                    selected={isSelected}
                    onClick={() => handleToggleStyle(id)}
                  >
                    {styleName}
                  </DropdownOption>
                );
              })}
            </div>

            <div className="flex justify-around p-3">
              <ContainedButton
                className="w-[45%]"
                color="secondary"
                onClick={handleCancel}
              >
                Cancel
              </ContainedButton>
              <ContainedButton className="w-[45%]" onClick={handleDone}>
                Done
              </ContainedButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
